import { FileText, Sparkles, BarChart3, GitBranch } from "lucide-react";
import { motion } from "framer-motion";
import { Section } from "@/components/layout/Section";
import { Container } from "@/components/layout/Container";
import { AnimatedSection } from "@/components/ui/animated-section";
import { slideInLeft, slideInRight } from "@/lib/animations";

interface FeatureShowcase {
  icon: React.ElementType;
  title: string;
  description: string;
  bullets: string[];
  imageAlt: string;
}

const features: FeatureShowcase[] = [
  {
    icon: FileText,
    title: "Intelligent Document Processing",
    description: "Upload any document format and watch our AI extract questions with remarkable accuracy. No more manual data entry or copy-pasting.",
    bullets: [
      "Supports PDF, Word, Excel, and images",
      "Handles scanned documents with OCR",
      "Preserves formatting and structure",
      "Batch processing for multiple files",
    ],
    imageAlt: "Document upload and processing interface",
  },
  {
    icon: Sparkles,
    title: "AI-Powered Question Detection",
    description: "Our advanced AI doesn't just extract text. It understands context, identifies question types, and suggests the best survey format.",
    bullets: [
      "Automatic question type classification",
      "Smart answer option detection",
      "Matrix and grid question support",
      "Rating scale and Likert recognition",
    ],
    imageAlt: "AI question detection demonstration",
  },
  {
    icon: GitBranch,
    title: "Advanced Survey Logic",
    description: "Create sophisticated surveys with branching logic, skip patterns, and conditional questions, all through an intuitive visual builder.",
    bullets: [
      "Visual logic flow builder",
      "Skip logic and branching",
      "Piping and dynamic text",
      "Quota management",
    ],
    imageAlt: "Survey logic builder interface",
  },
  {
    icon: BarChart3,
    title: "Real-Time Analytics",
    description: "Get instant insights as responses come in. Visualize data, export reports, and make data-driven decisions faster.",
    bullets: [
      "Live response dashboard",
      "Customizable charts and graphs",
      "Export to CSV, Excel, or SPSS",
      "Cross-tabulation analysis",
    ],
    imageAlt: "Analytics dashboard with charts",
  },
];

export function FeatureDeepDive() {
  return (
    <Section background="muted">
      <Container>
        <AnimatedSection className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">
            Everything You Need to Create <span className="text-gradient">Better Surveys</span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            Powerful features designed for researchers, marketers, and teams who need professional survey tools
          </p>
        </AnimatedSection>

        <div className="space-y-16 md:space-y-24">
          {features.map((feature, index) => (
            <div
              key={index}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
            >
              {/* Text Content - alternates sides on desktop with slide animations */}
              <motion.div 
                className={index % 2 === 1 ? "lg:order-2" : "lg:order-1"}
                variants={index % 2 === 0 ? slideInLeft : slideInRight}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <feature.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-lg text-muted-foreground mb-6">
                  {feature.description}
                </p>
                <ul className="space-y-3">
                  {feature.bullets.map((bullet, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg
                        className="h-5 w-5 text-primary flex-shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-muted-foreground">{bullet}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Image/Visual - alternates sides on desktop */}
              <motion.div 
                className={index % 2 === 1 ? "lg:order-1" : "lg:order-2"}
                variants={index % 2 === 0 ? slideInRight : slideInLeft}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
              >
                <div className="relative rounded-xl overflow-hidden shadow-lg border border-border bg-card hover-lift">
                  {/* Placeholder for feature screenshot */}
                  <div className="aspect-[4/3] bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center">
                    <div className="text-center p-8">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                        <feature.icon className="w-8 h-8 text-primary" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {feature.imageAlt}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
}
